import { Form, Table, Tag } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { RangePicker } = DatePicker;

const StudentAttendance = ({ setStep }) => {
  const user = useSelector(selectUser);
  const userId = user?._id;
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);

  const fetchStudentAttendance = async ({ queryKey }) => {
    const [, userId, dateRange] = queryKey;
    if (!userId || !dateRange[0] || !dateRange[1]) return null;

    const fromDate = dateRange[0].format("YYYY-MM-DD");
    const toDate = dateRange[1].format("YYYY-MM-DD");

    const res = await axios.get(
      `${serverUrl}/montessori/attendance/student/${userId}?fromDate=${fromDate}&toDate=${toDate}`,
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      },
    );
    return res.data;
  };

  const attendanceData = useQuery(
    ["student-attendance", userId, dateRange],
    fetchStudentAttendance,
    {
      enabled: !!userId && !!dateRange[0] && !!dateRange[1],
    },
  );

  // Client-side filtering when date range changes
  const getAttendanceList = useMemo(() => {
    const resultData = attendanceData?.data?.result;
    if (!resultData) return [];

    const studentData =
      Array.isArray(resultData) && resultData.length > 0
        ? resultData[0]
        : resultData?.data?.[0] || {};

    return [...(studentData?.attendance || [])].sort(
      (a, b) => moment(b?.date).valueOf() - moment(a?.date).valueOf(),
    );
  }, [attendanceData?.data?.result]);

  const columns = [
    {
      title: "Date",
      key: "date",
      align: "center",
      width: 150,
      render: (record) => (
        <div className="text-base font-medium">
          {moment(record?.date).format("DD MMM YYYY")}
        </div>
      ),
    },
    {
      title: "Check In",
      key: "check_in",
      align: "center",
      width: 150,
      render: (record) => (
        <div className="text-base">
          {record?.holiday?.isHoliday ? (
            <span className="text-blue-500 font-medium">Holiday</span>
          ) : record?.permission?.hasPermission ? (
            <span className="text-red-500 font-medium">❌ On Leave</span>
          ) : record?.is_checked_in ? (
            <div className="flex flex-col">
              <span className="text-green-600 font-medium">✓ Checked In</span>
              {record.check_in_time && (
                <span className="text-xs text-gray-400">
                  {moment(record.check_in_time).format("LT")}
                  {record.check_in_by?.username &&
                    ` by ${record.check_in_by.username}`}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">✗ Not Checked In</span>
          )}
        </div>
      ),
    },
    {
      title: "Check Out",
      key: "check_out",
      align: "center",
      width: 150,
      render: (record) => (
        <div className="text-base">
          {record?.holiday?.isHoliday ? (
            <span className="text-blue-500 font-medium">Holiday</span>
          ) : record?.permission?.hasPermission ? (
            <span className="text-red-500 font-medium">❌ On Leave</span>
          ) : record?.is_checked_out ? (
            <div className="flex flex-col">
              <span className="text-blue-600 font-medium">✓ Checked Out</span>
              {record.check_out_time && (
                <span className="text-xs text-gray-400">
                  {moment(record.check_out_time).format("LT")}
                  {record.check_out_by?.username &&
                    ` by ${record.check_out_by.username}`}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">✗ Not Checked Out</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      width: 120,
      render: (record) => {
        if (record?.holiday?.isHoliday) {
          return <Tag color="blue">Holiday</Tag>;
        }
        if (record?.permission?.hasPermission) {
          return <Tag color="orange">{record.permission.permissionType}</Tag>;
        }
        // A day with no record is not the same as a recorded absence: the
        // server sends gap-filled days without an _id, and showing those as
        // "Absent" would accuse the school of marking a child away when in
        // fact nobody ever took the register.
        if (!record?._id) {
          return <Tag color="default">Not marked</Tag>;
        }
        const morningExcused = !!record?.permission?.morningPermission && !record?.is_checked_in;
        const afternoonExcused = !!record?.permission?.afternoonPermission && !record?.is_checked_out;
        if (morningExcused || afternoonExcused) {
          const otherOk =
            (morningExcused || record?.is_checked_in) &&
            (afternoonExcused || record?.is_checked_out);
          return (
            <Tag
              color={
                morningExcused && afternoonExcused
                  ? "blue"
                  : otherOk
                    ? "orange"
                    : "red"
              }
            >
              {morningExcused && afternoonExcused
                ? "Permission"
                : otherOk
                  ? "Half Day (Permission)"
                  : "Absent"}
            </Tag>
          );
        }

        if (record?.is_checked_in && record?.is_checked_out) {
          return <Tag color="green">Present</Tag>;
        } else if (record?.is_checked_in || record?.is_checked_out) {
          return <Tag color="gold">Partial</Tag>;
        } else {
          return <Tag color="red">Absent</Tag>;
        }
      },
    },
  ];

  return (
    <div className="attendance">
      <div className="pt-8">
        <div className="bg-white p-10 rounded-2xl">
          <div className="text-2xl text-orange-500 font-semibold pb-8">
            My Attendance
          </div>
          <Form layout="vertical" className="w-1/2">
            <Form.Item label="Select Date Range" name="date" className="w-full">
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                format="DD-MM-YYYY"
                disabledDate={(d) => !d || d.isAfter(moment().endOf("day"))}
                className="w-full"
              />
            </Form.Item>
          </Form>
          <div className="pt-5 attendance-table">
            <Table
              columns={columns}
              dataSource={getAttendanceList}
              loading={attendanceData?.isLoading}
              rowKey={(record) => record.date}
              pagination={{ pageSize: 10 }}
              className="w-1/2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;
